import { DateTime } from '@ksyos/date-time';
import { Button, Card, Form, FormButtonRow, FormGroup, Heading, NumberInput, Select } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { PayoutRecipient } from '@common/enums';
import type { IService, IServiceActivity } from '@common/interfaces';
import type { AddEdit } from '@common/utils/utilityTypes';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import {
    type CompensationPayoutForServiceActivity,
    type CompensationPayoutForServiceActivityUpdatableFields,
    compensationPayoutForServiceActivityUpdatableFieldsSchema,
} from '@common/validation/compensationPayoutForServiceActivity';
import {
    type PayableServiceActivity,
    type PayableServiceActivityAddEdit,
    payableServiceActivitySchema,
    type PayableServiceActivityValidatedAddEdit,
    validCompensationPayoutRecipient,
} from '@common/validation/payableServiceActivity';
import { DateInput, OverlapMessage } from '@frontend/components/elements/common';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import {
    createServiceActivityCompensationPayout,
    deleteServiceActivityCompensationPayouts,
    getServiceActivityCompensationPayouts,
    updateOneServiceActivityCompensationPayout,
} from '@frontend/services/api/compensationPayoutApiService';
import {
    createPayableServiceActivity,
    getOnePayableActivity,
    updatePayableServiceActivity,
} from '@frontend/services/api/payableServiceActivityApiService';
import { getOneService, getServiceActivities } from '@frontend/services/api/servicesApiService';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    getValidationErrorTranslation,
    OverlapChecker,
    RateLimiter,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';

type ServiceCompensationPayoutsAddEdit = PayableServiceActivityAddEdit &
    AddEdit<CompensationPayoutForServiceActivityUpdatableFields>;

export function PayableServiceActivityCompensationPayoutAddEdit() {
    assertHasAuthorization(ManagerAuthorization.serviceAddEdit);
    const atMostOncePerSecond = new RateLimiter(1000);
    const overlapChecker = new OverlapChecker('/api/finance/payableServiceActivities');
    const { serviceId, id } = useParams();
    const translate = getLanguage();
    const abortSignal = useAbortSignal();
    const navigate = useNavigate();
    const [service, setService] = useState<IService>();
    const [serviceActivities, setServiceActivities] = useState<IServiceActivity[]>([]);
    const [serviceCompensationPayoutsAddEdit, setServiceCompensationPayoutsAddEdit] =
        useState<ServiceCompensationPayoutsAddEdit>({
            amount: null,
            conditionalServiceActivityId: null,
            endDate: null,
            payoutRecipient: null,
            serviceActivityId: null,
            startDate: null,
        });
    const [originalPayableActivity, setOriginalPayableActivity] = useState<PayableServiceActivity | null>();
    const [compensationPayout, setCompensationPayout] = useState<CompensationPayoutForServiceActivity | null>();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSaving, setIsSaving] = useState(false);
    const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
    const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);
    const [overlap, setOverlap] = useState<PayableServiceActivity | null>(null);

    const serviceActivityOptions = serviceActivities.map((serviceActivity) => ({
        label: serviceActivity.name,
        value: serviceActivity.id,
    }));
    const compensationPayoutRecipientOptions = [...validCompensationPayoutRecipient].map((recipient) => ({
        label: translate.compensationPayoutRecipientEnum[recipient as 'organization' | 'practitioner'],
        value: recipient,
    }));

    useEffect(() => {
        load();
    }, []);

    return (
        <PageTemplate width="medium">
            <Heading
                mb={[6, null, null, 12]}
                size="xlarge"
            >
                {`${id ? 'Bewerk' : 'Nieuwe'} ${service?.name} Vergoeding`}
            </Heading>
            <Card>
                <Card.Body>
                    <Form
                        onSubmit={(e) => {
                            e.preventDefault();
                            save();
                        }}
                        submitOnEnter={false}
                    >
                        {overlap && (
                            <OverlapMessage
                                endDate={overlap.endDate}
                                link={`${overlap.id}`}
                                message={translate.common.overlapMessage}
                                startDate={overlap.startDate}
                            />
                        )}
                        <FormGroup
                            hasErrors={errors.has('serviceActivityId')}
                            isRequired={true}
                            label="Activiteit"
                        >
                            <Select
                                error={getValidationErrorTranslation(errors.get('serviceActivityId'))}
                                name="serviceActivityId"
                                onChange={(option) => {
                                    handleChange('serviceActivityId', option?.value ?? null);
                                }}
                                options={serviceActivityOptions}
                                value={serviceActivityOptions.find(
                                    (item) => item.value === serviceCompensationPayoutsAddEdit.serviceActivityId
                                )}
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('conditionalServiceActivityId')}
                            label="Afhankelijk van"
                        >
                            <Select
                                error={getValidationErrorTranslation(errors.get('conditionalServiceActivityId'))}
                                isClearable={true}
                                name="conditionalServiceActivityId"
                                onChange={(option) => {
                                    handleChange('conditionalServiceActivityId', option?.value ?? null);
                                }}
                                options={serviceActivityOptions}
                                value={serviceActivityOptions.find(
                                    (item) =>
                                        item.value === serviceCompensationPayoutsAddEdit.conditionalServiceActivityId
                                )}
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('amount')}
                            label="Vergoeding"
                        >
                            <NumberInput
                                error={getValidationErrorTranslation(errors.get('amount'))}
                                isCurrency={true}
                                label="amount"
                                maximumFractionDigits={2}
                                onChange={(value) => handleChange('amount', value ? value * 100 : null)}
                                sx={{ maxWidth: '168px' }}
                                value={
                                    serviceCompensationPayoutsAddEdit.amount
                                        ? serviceCompensationPayoutsAddEdit.amount / 100
                                        : null
                                }
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('startDate')}
                            isRequired={true}
                            label="Begindatum"
                        >
                            <DateInput
                                error={errors.get('startDate')}
                                hasMaxWidth={true}
                                onChange={(value) => handleChange('startDate', value as string)}
                                placeholder={translate.common.dateFormat}
                                showOutOfBoundErrorsValidation={true}
                                size="large"
                                value={serviceCompensationPayoutsAddEdit.startDate as string}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('endDate')}
                            label="Einddatum"
                        >
                            <DateInput
                                error={errors.get('endDate')}
                                hasMaxWidth={true}
                                onChange={(value) => handleChange('endDate', value)}
                                placeholder={translate.common.dateFormat}
                                showOutOfBoundErrorsValidation={true}
                                size="large"
                                value={serviceCompensationPayoutsAddEdit.endDate}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={errors.has('payoutRecipient')}
                            isRequired={true}
                            label="Uitbetalen aan"
                        >
                            <Select
                                error={getValidationErrorTranslation(errors.get('payoutRecipient'))}
                                name="payoutRecipient"
                                onChange={(option) => {
                                    handleChange(
                                        'payoutRecipient',
                                        (option?.value as
                                            | PayoutRecipient.organization
                                            | PayoutRecipient.practitioner) ?? null
                                    );
                                }}
                                options={compensationPayoutRecipientOptions}
                                value={compensationPayoutRecipientOptions.find(
                                    (item) => item.value === serviceCompensationPayoutsAddEdit.payoutRecipient
                                )}
                            />
                        </FormGroup>
                        <FormButtonRow>
                            <Button
                                isDisabled={isSaving}
                                onClick={() => (hasChanges() ? setShowCancelConfirmationModal(true) : navigate(-1))}
                                variant="outline"
                            >
                                {translate.common.cancel}
                            </Button>
                            <Button
                                isDisabled={isSaving || !!overlap}
                                onClick={async () => {
                                    if (hasErrors()) {
                                        return;
                                    }
                                    hasChangesWithEffectOnFinance() ? setShowSubmitConfirmationModal(true) : save();
                                }}
                            >
                                {translate.common.save}
                            </Button>
                        </FormButtonRow>
                        <ConfirmationModal
                            cancelText={translate.common.cancelling}
                            confirmText={translate.common.proceed}
                            handleNo={() => setShowSubmitConfirmationModal(false)}
                            handleYes={() => {
                                setShowSubmitConfirmationModal(false);
                                save();
                            }}
                            isOpen={showSubmitConfirmationModal}
                            question="Deze wijziging heeft financiële gevolgen op reeds uitbetaalde processen. Neem contact op met Finance."
                            title="Financiële gevolgen ingestelde datum"
                        />
                        <ConfirmationModal
                            cancelText={translate.common.no}
                            confirmText={translate.common.yes}
                            handleNo={() => setShowCancelConfirmationModal(false)}
                            handleYes={() => {
                                setShowCancelConfirmationModal(false);
                                navigate(-1);
                            }}
                            isOpen={showCancelConfirmationModal}
                            question="Je hebt wijzigingen aangebracht die niet zijn opgeslagen. Weet je zeker dat je deze wilt verwijderen?"
                            title="Niet opgeslagen wijzigingen"
                        />
                    </Form>
                </Card.Body>
            </Card>
        </PageTemplate>
    );

    async function load() {
        if (!serviceId) {
            return;
        }

        if (id) {
            const existingPayableActivity = await getOnePayableActivity(id, abortSignal);
            const compensationPayouts = await getServiceActivityCompensationPayouts(
                {
                    serviceActivityId: existingPayableActivity.serviceActivityId,
                    serviceId,
                },
                abortSignal
            );

            const currentCompensationPayout = compensationPayouts.find(
                (existingCompensationPayout) =>
                    existingCompensationPayout.startDate === existingPayableActivity.startDate &&
                    existingCompensationPayout.endDate === existingPayableActivity.endDate
            );
            const existingPayableActivityWithCompensationPayout = {
                ...existingPayableActivity,
                amount: currentCompensationPayout?.amount ?? null,
            };

            setCompensationPayout(currentCompensationPayout ?? null);
            setServiceCompensationPayoutsAddEdit(existingPayableActivityWithCompensationPayout);
            setOriginalPayableActivity(existingPayableActivityWithCompensationPayout);
        }

        setService(await getOneService(serviceId, abortSignal));
        setServiceActivities(await getServiceActivities(serviceId, abortSignal));
    }

    async function save() {
        setIsSaving(true);

        const payableServiceActivityToSave = {
            conditionalServiceActivityId: serviceCompensationPayoutsAddEdit.conditionalServiceActivityId,
            endDate: serviceCompensationPayoutsAddEdit.endDate,
            payoutRecipient: serviceCompensationPayoutsAddEdit.payoutRecipient,
            serviceActivityId: serviceCompensationPayoutsAddEdit.serviceActivityId,
            startDate: serviceCompensationPayoutsAddEdit.startDate,
        } as PayableServiceActivityValidatedAddEdit;

        const compensationPayoutToSave = {
            amount: serviceCompensationPayoutsAddEdit.amount,
            endDate: serviceCompensationPayoutsAddEdit.endDate,
            serviceActivityId: serviceCompensationPayoutsAddEdit.serviceActivityId,
            startDate: serviceCompensationPayoutsAddEdit.startDate,
        } as CompensationPayoutForServiceActivityUpdatableFields;

        if (id) {
            await updatePayableServiceActivity(id, payableServiceActivityToSave, abortSignal);
        } else {
            await createPayableServiceActivity(payableServiceActivityToSave, abortSignal);
        }

        if (compensationPayout?.id) {
            if (compensationPayoutToSave.amount !== null) {
                await updateOneServiceActivityCompensationPayout(
                    compensationPayoutToSave,
                    compensationPayout.id,
                    abortSignal
                );
            } else {
                await deleteServiceActivityCompensationPayouts(compensationPayout.id, abortSignal);
            }
        } else if (compensationPayoutToSave.amount !== null) {
            await createServiceActivityCompensationPayout(compensationPayoutToSave, { serviceId }, abortSignal);
        }

        setIsSaving(false);
        navigate(`/services/${serviceId}?tab=serviceCompensationPayouts`);
    }

    async function handleChange<T extends keyof ServiceCompensationPayoutsAddEdit>(
        key: T,
        value: ServiceCompensationPayoutsAddEdit[T]
    ) {
        const updatedServiceCompensationPayout = {
            ...serviceCompensationPayoutsAddEdit,
            [key]: value,
        };
        if (key === 'amount' && value) {
            setErrors(
                updateValidationErrorsForOne(
                    compensationPayoutForServiceActivityUpdatableFieldsSchema,
                    updatedServiceCompensationPayout,
                    errors,
                    key
                )
            );
        } else if (
            ['serviceActivityId', 'conditionalServiceActivityId'].includes(key) &&
            updatedServiceCompensationPayout.serviceActivityId
        ) {
            setErrors(
                updateValidationErrorsForMany(payableServiceActivitySchema, updatedServiceCompensationPayout, errors, [
                    'serviceActivityId',
                    'conditionalServiceActivityId',
                ])
            );
        } else {
            setErrors(
                updateValidationErrorsForOne(
                    payableServiceActivitySchema,
                    updatedServiceCompensationPayout,
                    errors,
                    key
                )
            );
        }
        if (updatedServiceCompensationPayout.startDate && updatedServiceCompensationPayout.serviceActivityId) {
            await checkOverlap(updatedServiceCompensationPayout);
        }
        setServiceCompensationPayoutsAddEdit(updatedServiceCompensationPayout);
    }

    async function checkOverlap(payableServiceActivity: ServiceCompensationPayoutsAddEdit) {
        atMostOncePerSecond.callMeMaybe(async () => {
            const isOverlapped = await overlapChecker.check(
                { serviceActivityId: payableServiceActivity.serviceActivityId, serviceId },
                payableServiceActivity.startDate,
                payableServiceActivity.endDate,
                id
            );
            setOverlap(isOverlapped);
        });
    }

    function hasErrors() {
        let { errors: allErrors } = validate(payableServiceActivitySchema, serviceCompensationPayoutsAddEdit);
        if (serviceCompensationPayoutsAddEdit.amount) {
            allErrors = updateValidationErrorsForOne(
                compensationPayoutForServiceActivityUpdatableFieldsSchema,
                serviceCompensationPayoutsAddEdit,
                allErrors,
                'amount'
            );
        }
        if (allErrors.size > 0) {
            setErrors(allErrors);

            return true;
        }
    }

    function hasChanges() {
        return (
            (!originalPayableActivity &&
                Object.values(serviceCompensationPayoutsAddEdit).some((value) => value !== null)) ||
            (originalPayableActivity &&
                (serviceCompensationPayoutsAddEdit.amount !== (compensationPayout?.amount ?? null) ||
                    serviceCompensationPayoutsAddEdit.conditionalServiceActivityId !==
                        originalPayableActivity.conditionalServiceActivityId ||
                    serviceCompensationPayoutsAddEdit.endDate !== originalPayableActivity.endDate ||
                    serviceCompensationPayoutsAddEdit.payoutRecipient !== originalPayableActivity.payoutRecipient ||
                    serviceCompensationPayoutsAddEdit.serviceActivityId !== originalPayableActivity.serviceActivityId ||
                    serviceCompensationPayoutsAddEdit.startDate !== originalPayableActivity.startDate))
        );
    }

    function hasChangesWithEffectOnFinance() {
        return (
            hasChanges() &&
            serviceCompensationPayoutsAddEdit.startDate &&
            (DateTime.from(serviceCompensationPayoutsAddEdit.startDate).isSameOrBefore(DateTime.now()) ||
                (originalPayableActivity?.startDate &&
                    DateTime.from(originalPayableActivity.startDate).isSameOrBefore(DateTime.now())))
        );
    }
}
